export const USER_ROLES = {
  FREELANCER: 'freelancer',
  TEAM_MEMBER: 'team-member',
  TEAM_ADMIN: 'team-admin',
  SYSTEM_ADMIN: 'system-admin',
  SUPER_ADMIN: 'super-admin',
} as const

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES]

export interface MenuPermission {
  label: string
  paths: string[]
  iconType: string
  allowedRoles: UserRole[]
  requireEsn?: boolean
  hidden?: boolean
}

export interface RoutePermission {
  allowedRoles: UserRole[]
  deniedMessageKey?: string
}

export const menuPermissions: MenuPermission[] = [
  {
    label: 'Freelancers',
    paths: ['/freelancers'],
    iconType: 'freelancers',
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'Clients',
    paths: ['/clients'],
    iconType: 'clients',
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
    requireEsn: true,
  },
  {
    label: 'Missions',
    paths: ['/missions', '/missions/create', '/missions/review'],
    iconType: 'missions',
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'Activity Report',
    paths: ['/activity-report'],
    iconType: 'file-text',
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'Accounting',
    paths: ['/accounting'],
    iconType: 'receipt',
    allowedRoles: [USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'Availability',
    paths: ['/availability'],
    iconType: 'calendar',
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'OurPartners',
    paths: ['/our-partners'],
    iconType: 'handshake',
    allowedRoles: [USER_ROLES.FREELANCER],
  },
  {
    label: 'Company',
    paths: ['/company'],
    iconType: 'company',
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
    hidden: true,
  },
  {
    label: 'Users',
    paths: ['/users'],
    iconType: 'users',
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
    hidden: true,
  },
  {
    label: 'Reporting',
    paths: ['/reporting'],
    iconType: 'reporting',
    allowedRoles: [USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'Assets',
    paths: ['/assets'],
    iconType: 'handbook',
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'Devices & Tools',
    paths: ['/devices-tools'],
    iconType: 'devices',
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'FAQ',
    paths: ['/faq'],
    iconType: 'faq',
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'Files',
    paths: ['/files'],
    iconType: 'files',
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'Audit',
    paths: ['/audit'],
    iconType: 'audit',
    allowedRoles: [USER_ROLES.TEAM_ADMIN],
  },
  {
    label: 'Profile',
    paths: ['/profile'],
    iconType: 'profile',
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
    hidden: true,
  },
  {
    label: 'Super Admin',
    paths: ['/super-admin'],
    iconType: 'company',
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    label: 'Staffed Free',
    paths: ['/super-admin/staffed-free'],
    iconType: 'users',
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    label: 'KYC',
    paths: ['/super-admin/kyc'],
    iconType: 'shield-check',
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    label: 'E-invoicing',
    paths: ['/super-admin/e-invoicing'],
    iconType: 'receipt',
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    label: 'Emails',
    paths: ['/super-admin/emails'],
    iconType: 'mail',
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    label: 'Templates',
    paths: ['/super-admin/templates'],
    iconType: 'file-text',
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    label: 'Blog',
    paths: ['/super-admin/blog'],
    iconType: 'blog',
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
]

export const routePermissions: Record<string, RoutePermission> = {
  '/missions': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/missions/create': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/missions/create/:missionId': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/missions/review/:missionId': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/missions/edit/:missionId': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/missions/:missionId': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/clients': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/clients/:clientId': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/freelancers': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/freelancers/invite': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/freelancers/:id': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/activity-report': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/activity-report/create': {
    allowedRoles: [USER_ROLES.FREELANCER],
    deniedMessageKey: 'activityReport.create.permissionDenied',
  },
  '/activity-report/create/:id': {
    allowedRoles: [USER_ROLES.FREELANCER],
    deniedMessageKey: 'activityReport.create.permissionDenied',
  },
  '/activity-report/view/:id': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/availability': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/our-partners': {
    allowedRoles: [USER_ROLES.FREELANCER],
  },
  '/accounting': {
    allowedRoles: [USER_ROLES.TEAM_ADMIN],
  },
  '/billing/success': {
    allowedRoles: [USER_ROLES.TEAM_ADMIN, USER_ROLES.TEAM_MEMBER],
  },
  '/billing/cancel': {
    allowedRoles: [USER_ROLES.TEAM_ADMIN, USER_ROLES.TEAM_MEMBER],
  },
  '/templates/new': {
    allowedRoles: [USER_ROLES.TEAM_ADMIN, USER_ROLES.TEAM_MEMBER],
  },
  '/templates/:id': {
    allowedRoles: [USER_ROLES.TEAM_ADMIN, USER_ROLES.TEAM_MEMBER],
  },
  '/company': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/users': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/users/tag-management': {
    allowedRoles: [USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/reporting': {
    allowedRoles: [USER_ROLES.TEAM_ADMIN],
  },
  '/audit': {
    allowedRoles: [USER_ROLES.TEAM_ADMIN],
  },
  '/assets': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/devices-tools': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/faq': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/files': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/profile': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/settings': {
    allowedRoles: [USER_ROLES.FREELANCER, USER_ROLES.TEAM_MEMBER, USER_ROLES.TEAM_ADMIN],
  },
  '/super-admin': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/kyc': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/templates': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/templates/new': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/templates/:id': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/blog': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/blog/:postId': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/staffed-free': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/e-invoicing': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/emails': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/billing-mandates': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/super-admin/invoice-emission-jobs': {
    allowedRoles: [USER_ROLES.SUPER_ADMIN],
  },
  '/company-onboarding': {
    allowedRoles: [USER_ROLES.TEAM_ADMIN],
  },
}

export const publicRoutes = [
  '/login',
  '/sign-on',
  '/signup/company',
  '/auth/callback',
  '/forgot-password',
  '/reset-password',
  '/onboarding',
  '/activity-report/preview',
  '/activity-report/client-validation',
  '/availability/respond',
  '/portage/onboarding',
  '/portage/questionnaire',
  '/portage/mission',
  '/client/contract',
  '/client/dashboard',
]

export const buttonPermissions: Record<string, RoutePermission> = {}

/** Sidebar items super-admin can disable per team (matches sidebar menu labels). */
export const TEAM_CONFIGURABLE_SIDEBAR_ITEMS = [
  'Activity Report',
  'Availability',
  'Accounting',
  'Reporting',
  'Files',
  'Audit',
  'Assets',
  'Devices & Tools',
  'FAQ',
] as const

/** Always visible — not disableable per team. */
export const TEAM_SIDEBAR_ALWAYS_VISIBLE_ITEMS = ['OurPartners', 'Freelancers', 'Missions'] as const

/** Visibility controlled by is_esn — not disableable per team. */
export const TEAM_SIDEBAR_ESN_CONTROLLED_ITEMS = ['Clients'] as const

export type TeamConfigurableSidebarItem = (typeof TEAM_CONFIGURABLE_SIDEBAR_ITEMS)[number]

export function getSuperAdminFixedSidebarItems(
  groupLabelKey: string,
  isEsn: boolean
): readonly string[] {
  if (groupLabelKey !== 'groupOperations') return []

  return isEsn ? ['Freelancers', 'Clients', 'Missions'] : ['Freelancers', 'Missions']
}

/** Groups for super-admin sidebar config UI (mirrors Sidebar.jsx MENU_GROUPS). */
export const TEAM_SIDEBAR_CONFIG_GROUPS = [
  {
    labelKey: 'groupOperations',
    items: ['Activity Report', 'Availability'],
  },
  {
    labelKey: 'groupAccounting',
    items: ['Accounting', 'Reporting', 'Files', 'Audit'],
  },
  {
    labelKey: 'groupResources',
    items: ['Assets', 'Devices & Tools', 'FAQ'],
  },
] as const

const SIDEBAR_LABEL_ROUTE_PREFIXES: Record<string, string[]> = {
  'Activity Report': ['/activity-report'],
  Availability: ['/availability'],
  Accounting: ['/accounting'],
  Reporting: ['/reporting'],
  Files: ['/files'],
  Audit: ['/audit'],
  Assets: ['/assets'],
  'Devices & Tools': ['/devices-tools'],
  FAQ: ['/faq'],
}

const NON_DISABLEABLE_LABELS = new Set<string>([
  ...TEAM_SIDEBAR_ALWAYS_VISIBLE_ITEMS,
  ...TEAM_SIDEBAR_ESN_CONTROLLED_ITEMS,
])

export function isSidebarItemDisabled(
  label: string,
  disabledItems: string[] | undefined
): boolean {
  if (NON_DISABLEABLE_LABELS.has(label)) return false
  return Boolean(disabledItems?.includes(label))
}

export function isRouteDisabledByTeamSidebar(
  pathname: string,
  disabledItems: string[] | undefined
): boolean {
  if (!disabledItems?.length) return false

  for (const label of disabledItems) {
    const prefixes = SIDEBAR_LABEL_ROUTE_PREFIXES[label]
    if (!prefixes) continue
    if (prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
      return true
    }
  }

  return false
}
